import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  X, Trash2, Minus, Plus, CreditCard, ShoppingBag, ArrowLeft, AlertTriangle, Truck,
  MessageCircle, Mail, HelpCircle, CalendarDays, FileText, ArrowRight,
} from "lucide-react";
import { useCart } from "@/context/CartContext";
import { createCheckout, getQuote, SOCIALS, SHOP_CONTACT_EMAIL } from "@/lib/api";
import { toast } from "sonner";

const emptyCustomer = { name: "", email: "", phone: "", occasion: "", notes: "", postal_code: "" };
const STEPS = ["Details", "Shipping", "Payment"];
const isBouquetId = (id) => id.startsWith("dipped-") || id.startsWith("tray-") || id.startsWith("box-");

const InfoTip = ({ text, testid }) => (
  <span className="relative group inline-flex align-middle" tabIndex={0} data-testid={testid}>
    <HelpCircle className="h-4 w-4 text-candy-teal cursor-help" />
    <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 bg-black text-white text-[11px] font-medium rounded-lg p-2.5 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity z-20 leading-snug shadow-xl">
      {text}
    </span>
  </span>
);

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, total } = useCart();
  const [step, setStep] = useState("cart");
  const [cstep, setCstep] = useState(1);
  const [customer, setCustomer] = useState(emptyCustomer);
  const [fulfillment, setFulfillment] = useState("pickup");
  const [ack, setAck] = useState(false);
  const [tcAck, setTcAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [payError, setPayError] = useState(false);
  const [quote, setQuote] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [bouquetDate, setBouquetDate] = useState("");

  const hasBouquet = items.some((i) => isBouquetId(i.product_id));
  const itemCount = items.reduce((a, i) => a + i.quantity, 0);

  useEffect(() => {
    if (!open || step !== "checkout" || items.length === 0) return;
    let active = true;
    getQuote(buildPayload())
      .then((q) => active && setQuote(q))
      .catch(() => active && setQuote(null));
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, fulfillment, items, customer.postal_code, coupon, customer.email]);

  const close = () => {
    setOpen(false);
    setTimeout(() => { setStep("cart"); setCstep(1); }, 300);
  };

  const buildPayload = () => ({
    items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size, color: i.color, customization: i.customization, bulk_lbs: i.bulk_lbs })),
    customer: { ...customer, bouquet_date: bouquetDate },
    fulfillment,
    postal_code: customer.postal_code,
    coupon,
    email: customer.email,
    origin_url: window.location.origin,
  });

  const minBouquetDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 3);
    return d.toISOString().split("T")[0];
  };

  const zipValid = () => /^\d{5}$/.test((customer.postal_code || "").trim());

  const next = () => {
    if (cstep === 1 && (!customer.name || !customer.email)) {
      toast.error("Name and email are required");
      return;
    }
    if (cstep === 2) {
      if (fulfillment === "delivery" && !zipValid()) {
        toast.error("Enter a valid 5-digit ZIP for delivery");
        return;
      }
      if (hasBouquet && !bouquetDate) {
        toast.error("Please choose a ready-by date (3+ days out)");
        return;
      }
    }
    setCstep((c) => Math.min(3, c + 1));
  };

  const back = () => {
    if (cstep === 1) setStep("cart");
    else setCstep((c) => Math.max(1, c - 1));
  };

  const validatePay = () => {
    if (!customer.name || !customer.email) { toast.error("Name and email are required"); setCstep(1); return false; }
    if (fulfillment === "delivery" && !zipValid()) { toast.error("Enter a valid 5-digit ZIP for delivery"); setCstep(2); return false; }
    if (hasBouquet && !bouquetDate) { toast.error("Please choose a ready-by date (3+ days out)"); setCstep(2); return false; }
    if (!ack) { toast.error("Please acknowledge the shipping policy"); return false; }
    if (!tcAck) { toast.error("Please agree to the Terms & Conditions"); return false; }
    return true;
  };

  const handlePay = async () => {
    if (!validatePay()) return;
    setSubmitting(true);
    setPayError(false);
    try {
      const res = await createCheckout(buildPayload());
      if (res.url) window.location.href = res.url;
      else { toast.error("Could not start checkout"); setPayError(true); setSubmitting(false); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setPayError(true);
      setSubmitting(false);
    }
  };

  const field = (key, label, type = "text", ph = "") => (
    <div>
      <label className="font-mono text-[11px] font-bold uppercase tracking-widest">{label}</label>
      <input
        data-testid={`checkout-${key}`}
        type={type}
        value={customer[key]}
        placeholder={ph}
        onChange={(e) => setCustomer({ ...customer, [key]: e.target.value })}
        className="mt-1 w-full bg-candy-card border-4 border-black rounded-lg px-3 py-2.5 font-medium focus:outline-none focus:shadow-brutal-teal transition-shadow"
      />
    </div>
  );

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 bg-black/50 z-[60]"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full sm:w-[440px] bg-candy-bg border-l-4 border-black z-[70] flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-5 border-b-4 border-black bg-candy-pink text-white">
              <h3 className="font-display text-3xl tracking-wide flex items-center gap-2">
                {step === "cart" ? (<><ShoppingBag className="h-6 w-6" /> Your Loot</>) : "Checkout"}
              </h3>
              <button data-testid="close-cart-btn" onClick={close} className="border-2 border-black bg-candy-card text-candy-cream rounded-lg p-1.5 hover:rotate-90 transition-transform">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {items.length === 0 ? (
                <div className="text-center mt-16">
                  <p className="font-heading text-xl font-600">Your cart is empty</p>
                  <p className="text-sm text-candy-cream/60 mt-2">Go steal some sweets from the vault!</p>
                </div>
              ) : step === "cart" ? (
                items.map((i) => (
                  <div
                    key={`${i.product_id}-${i.size || ""}-${i.color || ""}-${(i.customization || "").slice(0, 12)}`}
                    data-testid={`cart-item-${i.product_id}`}
                    className="flex gap-3 bg-candy-card border-4 border-black rounded-xl p-3 shadow-brutal-sm"
                  >
                    <img src={i.image} alt={i.name} className="h-16 w-16 object-cover rounded-lg border-2 border-black" />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <p className="font-heading font-600 text-sm leading-tight">{i.name}</p>
                        <button data-testid={`remove-${i.product_id}`} onClick={() => removeItem(i.product_id, i.size, i.color, i.customization)} className="text-candy-pink">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      {(i.size || i.color) && (
                        <p className="text-xs font-mono">
                          {i.color ? `Color: ${i.color}` : ""}{i.color && i.size ? " · " : ""}{i.size ? `Size: ${i.size}` : ""}
                        </p>
                      )}
                      {i.customization && (
                        <p className="text-xs text-candy-cream/70 mt-0.5 leading-snug" data-testid={`custom-${i.product_id}`}>{i.customization}</p>
                      )}
                      <div className="flex justify-between items-center mt-2">
                        <div className="flex items-center gap-2">
                          <button data-testid={`dec-${i.product_id}`} onClick={() => updateQty(i.product_id, i.size, i.color, i.customization, i.quantity - 1)} className="border-2 border-black rounded-md p-0.5">
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="font-bold w-5 text-center" data-testid={`qty-${i.product_id}`}>{i.quantity}</span>
                          <button data-testid={`inc-${i.product_id}`} onClick={() => updateQty(i.product_id, i.size, i.color, i.customization, i.quantity + 1)} className="border-2 border-black rounded-md p-0.5">
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <span className="font-display text-xl text-candy-pink">${(i.price * i.quantity).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="space-y-4">
                  {/* Progress bar */}
                  <div className="flex items-center" data-testid="checkout-progress">
                    {STEPS.map((label, idx) => {
                      const n = idx + 1;
                      const active = cstep === n;
                      const done = cstep > n;
                      return (
                        <React.Fragment key={label}>
                          <div className="flex items-center gap-1.5" data-testid={`progress-step-${n}`}>
                            <span className={`h-6 w-6 rounded-full border-2 border-black flex items-center justify-center font-bold text-[11px] ${done ? "bg-candy-teal text-black" : active ? "bg-candy-pink text-white" : "bg-candy-card text-candy-cream/40"}`}>
                              {done ? "✓" : n}
                            </span>
                            <span className={`text-[10px] font-bold uppercase tracking-wider ${active ? "text-candy-pink" : done ? "text-candy-cream" : "text-candy-cream/40"}`}>{label}</span>
                          </div>
                          {n < 3 && <div className={`flex-1 h-0.5 mx-1.5 ${cstep > n ? "bg-candy-teal" : "bg-black/15"}`} />}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  {/* Always-visible cart summary */}
                  <div className="bg-candy-card border-4 border-black rounded-xl p-3" data-testid="checkout-cart-summary">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-mono text-[11px] font-bold uppercase tracking-widest">In your bag ({itemCount})</p>
                      <span className="font-display text-lg text-candy-pink">${(quote ? quote.total : total).toFixed(2)}</span>
                    </div>
                    <div className="space-y-1.5 max-h-28 overflow-y-auto">
                      {items.map((i) => (
                        <div key={`sum-${i.product_id}-${i.size || ""}-${(i.customization || "").slice(0, 8)}`} className="flex items-center gap-2 text-xs">
                          <img src={i.image} alt="" className="h-8 w-8 rounded border-2 border-black object-cover" />
                          <span className="flex-1 font-medium leading-tight">{i.quantity}× {i.name}</span>
                          <span className="font-bold">${(i.price * i.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* STEP 1 — Details */}
                  {cstep === 1 && (
                    <div className="space-y-4" data-testid="checkout-step-details">
                      {field("name", "Full Name *", "text", "Jane Bandit")}
                      {field("email", "Email *", "email", "you@email.com")}
                      {field("phone", "Phone", "tel", "(555) 000-0000")}
                      {field("occasion", "Occasion (bouquets)", "text", "Birthday, Anniversary…")}
                    </div>
                  )}

                  {/* STEP 2 — Shipping */}
                  {cstep === 2 && (
                    <div className="space-y-4" data-testid="checkout-step-shipping">
                      <div>
                        <label className="font-mono text-[11px] font-bold uppercase tracking-widest flex items-center gap-1.5">
                          Fulfillment
                          <InfoTip testid="shipping-info-tip" text="We ship every Friday before 12:00 PM (noon) CST. Expedited orders must be requested at least 48 hours in advance." />
                        </label>
                        <div className="mt-1 grid grid-cols-2 gap-2">
                          {[["pickup", "Local Pickup"], ["delivery", "Delivery"]].map(([val, label]) => (
                            <button
                              key={val}
                              type="button"
                              data-testid={`fulfillment-${val}`}
                              onClick={() => setFulfillment(val)}
                              className={`border-4 border-black rounded-lg py-2.5 font-bold uppercase text-xs tracking-wider transition-transform active:translate-x-0.5 active:translate-y-0.5 ${fulfillment === val ? "bg-candy-pink text-white shadow-brutal-sm" : "bg-candy-card"}`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {fulfillment === "delivery" && (
                        <div>
                          <label className="font-mono text-[11px] font-bold uppercase tracking-widest">Delivery ZIP Code *</label>
                          <input
                            data-testid="checkout-postal_code"
                            type="text" inputMode="numeric" maxLength={5}
                            value={customer.postal_code}
                            placeholder="e.g. 55401"
                            onChange={(e) => setCustomer({ ...customer, postal_code: e.target.value.replace(/[^0-9]/g, "") })}
                            className="mt-1 w-full bg-candy-card border-4 border-black rounded-lg px-3 py-2.5 font-medium focus:outline-none focus:shadow-brutal-teal transition-shadow"
                          />
                          <p className="text-[11px] text-candy-cream/60 font-medium mt-1">Flat $12.99 shipping on delivery orders.</p>
                          {quote && quote.city && quote.state && (
                            <p className="text-[11px] font-bold text-candy-teal mt-1" data-testid="zip-city-state">📍 {quote.city}, {quote.state}</p>
                          )}
                        </div>
                      )}

                      <div>
                        <label className="font-mono text-[11px] font-bold uppercase tracking-widest">Notes / Delivery Address</label>
                        <textarea
                          data-testid="checkout-notes"
                          value={customer.notes}
                          onChange={(e) => setCustomer({ ...customer, notes: e.target.value })}
                          rows={3}
                          placeholder="Delivery address, allergies, custom requests…"
                          className="mt-1 w-full bg-candy-card border-4 border-black rounded-lg px-3 py-2.5 font-medium focus:outline-none focus:shadow-brutal-teal transition-shadow"
                        />
                      </div>

                      {hasBouquet && (
                        <div className="bg-candy-gold/40 border-4 border-black rounded-xl p-3" data-testid="bouquet-date-section">
                          <label className="font-heading font-700 text-sm flex items-center gap-1.5">
                            <CalendarDays className="h-4 w-4" /> Ready-By Date (3+ days out) *
                          </label>
                          <p className="text-[11px] font-medium mt-1 leading-snug">Made-to-order items need 48–72 hrs of prep. Pick your pickup/delivery date:</p>
                          <input
                            data-testid="bouquet-date"
                            type="date"
                            min={minBouquetDate()}
                            value={bouquetDate}
                            onChange={(e) => setBouquetDate(e.target.value)}
                            className="mt-2 w-full bg-candy-card border-4 border-black rounded-lg px-3 py-2.5 font-medium focus:outline-none focus:shadow-brutal-teal transition-shadow"
                          />
                        </div>
                      )}

                      <div>
                        <label className="font-mono text-[11px] font-bold uppercase tracking-widest">Promo Code</label>
                        <input
                          data-testid="checkout-coupon"
                          type="text"
                          value={coupon}
                          placeholder="Have a code? Enter it here"
                          onChange={(e) => setCoupon(e.target.value.toUpperCase().replace(/\s/g, ""))}
                          className="mt-1 w-full bg-candy-card border-4 border-black rounded-lg px-3 py-2.5 font-medium uppercase tracking-wider focus:outline-none focus:shadow-brutal-teal transition-shadow"
                        />
                        {coupon && quote && (
                          quote.coupon_applied ? (
                            <p className="text-[11px] font-bold text-green-600 mt-1" data-testid="coupon-applied">
                              {quote.coupon_type === "discount"
                                ? `✅ ${quote.coupon_code} applied — you save $${(quote.discount || 0).toFixed(2)}!`
                                : "✅ Code applied — free shipping!"}
                            </p>
                          ) : quote.coupon_valid && quote.coupon_type === "free_shipping" ? (
                            <p className="text-[11px] font-bold text-candy-cream/60 mt-1">This code gives free shipping on delivery orders.</p>
                          ) : quote.coupon_reason === "already_used" ? (
                            <p className="text-[11px] font-bold text-candy-pink mt-1" data-testid="coupon-invalid">You've already used this code — one per customer.</p>
                          ) : quote.coupon_reason === "expired" ? (
                            <p className="text-[11px] font-bold text-candy-pink mt-1" data-testid="coupon-invalid">Sorry — this code has expired or reached its limit.</p>
                          ) : quote.coupon_reason === "no_eligible_items" ? (
                            <p className="text-[11px] font-bold text-candy-pink mt-1" data-testid="coupon-invalid">{quote.coupon_prize_item ? `Add ${quote.coupon_prize_item} to your cart to use this prize code.` : "This code doesn't apply to the items in your cart."}</p>
                          ) : quote.coupon_reason === "wrong_email" ? (
                            <p className="text-[11px] font-bold text-candy-pink mt-1" data-testid="coupon-invalid">This prize code belongs to a different email. Use the email you spun with.</p>
                          ) : (
                            <p className="text-[11px] font-bold text-candy-pink mt-1" data-testid="coupon-invalid">That code isn't valid or has expired.</p>
                          )
                        )}
                      </div>

                      <div className="bg-candy-teal/20 border-4 border-black rounded-xl p-3" data-testid="shipping-schedule">
                        <p className="font-heading font-700 text-sm flex items-center gap-1.5"><Truck className="h-4 w-4" /> Shipping Notice</p>
                        <ul className="mt-1.5 text-[11px] font-medium space-y-1 list-disc pl-4 leading-snug">
                          <li>We ship <b>every Friday before 12:00 PM CST</b>.</li>
                          <li>Expedited orders must be requested at least <b>48 hours in advance</b>.</li>
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* STEP 3 — Payment */}
                  {cstep === 3 && (
                    <div className="space-y-4" data-testid="checkout-step-payment">
                      <div className="bg-candy-gold/40 border-4 border-black rounded-xl p-4">
                        <div className="flex items-center gap-2 font-heading font-700 text-sm uppercase">
                          <AlertTriangle className="h-5 w-5" /> Shipping Care Notice
                        </div>
                        <p className="text-xs mt-2 leading-relaxed font-medium">
                          Chocolate and candy can be damaged in transit. We do our best to ship everything in great condition. If your order arrives ruined or you're unsatisfied, send us a photo and we'll issue a <b>full refund AND resend your order</b> to you — a <b>one-time grace</b> per customer.
                        </p>
                        <label className="flex items-start gap-2 mt-3 cursor-pointer">
                          <input data-testid="shipping-ack" type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 h-5 w-5 border-2 border-black accent-candy-pink" />
                          <span className="text-xs font-bold">I understand & accept the shipping / one-time grace policy.</span>
                        </label>
                      </div>

                      <div className="bg-candy-card border-4 border-black rounded-xl p-4" data-testid="terms-section">
                        <p className="font-heading font-700 text-sm flex items-center gap-1.5"><FileText className="h-4 w-4" /> Terms & Conditions</p>
                        <ul className="mt-1.5 text-[11px] font-medium space-y-1 list-disc pl-4 leading-snug">
                          <li>All orders require <b>12–24 hours</b> for processing prior to shipping.</li>
                          <li>Made-to-order candy bouquets require <b>48–72 hours</b> for prep.</li>
                          <li>We ship <b>every Friday before 12:00 PM CST</b>; expedited orders must be requested at least <b>48 hours in advance</b>.</li>
                          <li>Damaged-in-transit orders are covered by a one-time grace refund/resend per customer.</li>
                        </ul>
                        <label className="flex items-start gap-2 mt-3 cursor-pointer">
                          <input data-testid="terms-ack" type="checkbox" checked={tcAck} onChange={(e) => setTcAck(e.target.checked)} className="mt-0.5 h-5 w-5 border-2 border-black accent-candy-pink" />
                          <span className="text-xs font-bold">I agree to the Terms & Conditions.</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t-4 border-black p-5 bg-candy-card space-y-3">
                {step === "checkout" && quote ? (
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm font-medium">
                      <span className="text-candy-cream/70">Subtotal</span>
                      <span data-testid="cart-subtotal">${quote.subtotal.toFixed(2)}</span>
                    </div>
                    {quote.discount > 0 && (
                      <div className="flex justify-between text-sm font-bold text-green-600">
                        <span className="flex items-center gap-1.5">{quote.coupon_code}</span>
                        <span data-testid="cart-discount">-${quote.discount.toFixed(2)}</span>
                      </div>
                    )}
                    {quote.weight_fee > 0 && (
                      <div className="flex justify-between text-sm font-medium">
                        <span className="text-candy-cream/70">Weight add-on</span>
                        <span data-testid="cart-weight-fee">${quote.weight_fee.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-medium">
                      <span className="text-candy-cream/70 flex items-center gap-1.5">
                        <Truck className="h-4 w-4" /> Shipping {fulfillment === "pickup" ? "(pickup)" : ""}
                      </span>
                      <span data-testid="cart-shipping" className="font-bold">
                        {quote.shipping_charged > 0 ? `$${quote.shipping_charged.toFixed(2)}` : "FREE"}
                      </span>
                    </div>
                    {fulfillment === "delivery" && quote.zip_ok && quote.miles != null && (
                      <p className="text-[11px] text-candy-cream/60 font-medium capitalize">{quote.zone} rate · ~{quote.miles} mi from our shop</p>
                    )}
                    <div className="flex justify-between items-center pt-1.5 border-t-2 border-black/10">
                      <span className="font-heading font-600 uppercase text-sm tracking-widest">Total</span>
                      <span className="font-display text-4xl text-candy-pink" data-testid="cart-total">${quote.total.toFixed(2)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-center">
                    <span className="font-heading font-600 uppercase text-sm tracking-widest">
                      {step === "checkout" ? "Total" : "Subtotal"}
                    </span>
                    <span className="font-display text-4xl text-candy-pink" data-testid="cart-total">${total.toFixed(2)}</span>
                  </div>
                )}

                {step === "cart" ? (
                  <button
                    data-testid="go-to-checkout-btn"
                    onClick={() => { setStep("checkout"); setCstep(1); }}
                    className="w-full bg-candy-pink text-white font-bold uppercase tracking-wider py-3.5 border-4 border-black rounded-xl shadow-brutal-sm active:translate-x-1 active:translate-y-1 active:shadow-none transition-transform duration-150"
                  >
                    Pull the Heist 🎁
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      data-testid="checkout-back-btn"
                      onClick={back}
                      className="flex items-center justify-center gap-1 bg-candy-card border-4 border-black rounded-xl px-4 py-3.5 font-bold uppercase text-xs tracking-wider shadow-brutal-sm active:translate-x-0.5 active:translate-y-0.5 active:shadow-none transition-transform"
                    >
                      <ArrowLeft className="h-4 w-4" /> Back
                    </button>
                    {cstep < 3 ? (
                      <button
                        data-testid="checkout-continue-btn"
                        onClick={next}
                        className="flex-1 flex items-center justify-center gap-1.5 bg-candy-pink text-white font-bold uppercase tracking-wider py-3.5 border-4 border-black rounded-xl shadow-brutal-sm active:translate-x-1 active:translate-y-1 active:shadow-none transition-transform duration-150"
                      >
                        Continue <ArrowRight className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        data-testid="pay-card-btn"
                        disabled={submitting || (fulfillment === "delivery" && !(quote && quote.zip_ok))}
                        onClick={handlePay}
                        className="flex-1 bg-candy-pink text-white font-bold uppercase tracking-wider py-3.5 border-4 border-black rounded-xl shadow-brutal-sm active:translate-x-1 active:translate-y-1 active:shadow-none transition-transform duration-150 flex items-center justify-center gap-2 disabled:opacity-60"
                      >
                        <CreditCard className="h-5 w-5" /> {submitting ? "Processing…" : "Pay with Card"}
                      </button>
                    )}
                  </div>
                )}

                {step === "checkout" && cstep === 3 && (
                  <>
                    <div className="mt-3 bg-candy-card border-4 border-black rounded-xl p-3" data-testid="what-happens-next">
                      <p className="font-heading font-700 text-sm mb-2">What happens next</p>
                      <ol className="space-y-1.5 text-xs font-medium">
                        <li className="flex items-center gap-2"><span className="font-display text-candy-pink text-base">1.</span> Order received 🍬</li>
                        <li className="flex items-center gap-2"><span className="font-display text-candy-pink text-base">2.</span> Hand-packed for you (12–24 hrs) 📦</li>
                        <li className="flex items-center gap-2"><span className="font-display text-candy-pink text-base">3.</span> Tracking email sent! 🚚</li>
                      </ol>
                    </div>

                    <div className="mt-3 bg-candy-card border-4 border-black rounded-xl p-3 flex items-center gap-3" data-testid="checkout-qr">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&margin=0&data=${encodeURIComponent(window.location.origin)}`}
                        alt="Scan to open shop on your phone"
                        width={70} height={70}
                        className="border-2 border-black rounded-lg shrink-0"
                      />
                      <div>
                        <p className="font-heading font-700 text-sm">📱 Rather use your phone?</p>
                        <p className="text-[11px] font-medium text-candy-cream/70 leading-snug">Scan this code to open the shop on your phone and finish checkout there.</p>
                      </div>
                    </div>

                    <div
                      data-testid="payment-fallback"
                      className={`mt-3 rounded-xl transition-all ${payError ? "bg-candy-yellow border-4 border-black p-3 shadow-brutal-sm" : "text-center"}`}
                    >
                      {payError && (
                        <p className="font-heading font-700 text-sm text-black text-center mb-1.5">Payment trouble? We've still got you! 🦝</p>
                      )}
                      <p className={`text-[11px] font-medium text-center ${payError ? "text-black/80 mb-2" : "text-candy-cream/60"}`}>
                        {payError ? "Message us and we'll help you finish your order right away:" : "Having payment issues? "}
                        {!payError && (
                          <button type="button" data-testid="payment-fallback-toggle" onClick={() => setPayError(true)} className="underline font-bold text-candy-pink">
                            Message us to complete your order
                          </button>
                        )}
                      </p>
                      {payError && (
                        <div className="flex items-center justify-center gap-2">
                          <a data-testid="fallback-dm-link" href={SOCIALS.facebook} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 bg-candy-card border-4 border-black rounded-lg px-3 py-2 font-bold uppercase text-[11px] tracking-wider shadow-brutal-sm active:translate-x-0.5 active:translate-y-0.5 active:shadow-none transition-transform">
                            <MessageCircle className="h-4 w-4" /> DM us
                          </a>
                          <a data-testid="fallback-email-link" href={`mailto:${SHOP_CONTACT_EMAIL}?subject=${encodeURIComponent("Order help — payment issue")}&body=${encodeURIComponent("Hi Sweet Tooth Bandit! I had trouble paying online and would love to complete my order. Here's what I want:\n\n")}`} className="flex items-center gap-1.5 bg-candy-card border-4 border-black rounded-lg px-3 py-2 font-bold uppercase text-[11px] tracking-wider shadow-brutal-sm active:translate-x-0.5 active:translate-y-0.5 active:shadow-none transition-transform">
                            <Mail className="h-4 w-4" /> Email us
                          </a>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
